import Router from 'next/router';
import { type KeyboardEvent, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useTrackClickedPublicBoardLink } from '@air/analytics';
import { ChevronsLeftIcon } from '@air/design-icons';

import { AssetModalHeaderAirLogoLink } from '~/components/AssetModalV2/Header/components/AssetModalHeaderAirLogoLink';
import { PublicTopBarAccountInfo } from '~/components/Layouts/PublicBoardLayout/PublicTopBarAccountInfo';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { createAllContentAssetRoute } from '~/constants/routes';
import { useShowPublicAirLogo } from '~/hooks/useShowPublicAirLogo';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';

export const LoggedInBanner = memo(() => {
  const { workspaceName } = usePublicWorkspace();
  const clipId = useSelector(centralizedClipIdSelector);
  const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();
  const { data: account } = useAccountContext();
  const { showAirLogo } = useShowPublicAirLogo();

  const onClick = useCallback(() => {
    trackClickedPublicBoardLink({
      name: 'clicked view in workspace',
      page_area: 'banner',
      page_type: 'public asset',
    });

    Router.push(createAllContentAssetRoute(clipId));
  }, [clipId, trackClickedPublicBoardLink]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <div
      className="relative flex h-14 w-full shrink-0 items-center justify-between ads:bg-base bg-pigeon-900 px-3"
      data-testid="PUBLIC_ASSET_MOBILE_LOGGED_IN_BANNER"
    >
      {/* biome-ignore lint/a11y/useSemanticElements: We're using a div instead of a button to prevent invalid DOM nesting (the label is a nested div). */}
      <div
        className="flex cursor-pointer items-center justify-start ads:text-metadata text-gray-10 ads:hover:text-primary hover:text-gray-12"
        data-testid="PUBLIC_ASSET_MOBILE_WORKSPACE_INFO"
        onClick={onClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <ChevronsLeftIcon size="md" className="mr-1 text-inherit" />
        <div className="truncate font-medium text-16 text-inherit">View in {workspaceName}</div>
      </div>
      <div className="flex items-center gap-4">
        {!!account && <PublicTopBarAccountInfo account={account} />}
        {showAirLogo && <AssetModalHeaderAirLogoLink />}
      </div>
    </div>
  );
});

LoggedInBanner.displayName = 'LoggedInBanner';
